import React, { createContext, useContext, useState, useEffect } from "react";
import axios from "axios";

const AdminContext = createContext();

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const useAdmin = () => {
  const context = useContext(AdminContext);
  if (!context) {
    throw new Error("useAdmin must be used within an AdminProvider");
  }
  return context;
};

export const AdminProvider = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [adminUser, setAdminUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem("admin_token"));

  // Configure axios defaults if token exists
  useEffect(() => {
    if (token) {
      axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;
      verifyToken();
    } else {
      setIsLoading(false);
    }
  }, [token]);

  const verifyToken = async () => {
    try {
      const response = await axios.get(`${API}/admin/verify`);
      setIsAuthenticated(true);
      setAdminUser(response.data.username);
    } catch (error) {
      console.error("Token verification failed:", error);
      logout();
    } finally {
      setIsLoading(false);
    }
  };

  const login = async (username, password) => {
    try {
      const response = await axios.post(`${API}/admin/login`, {
        username,
        password
      });
      
      const { access_token, message } = response.data;
      
      // Store token
      localStorage.setItem("admin_token", access_token);
      setToken(access_token);
      
      // Set axios default header
      axios.defaults.headers.common["Authorization"] = `Bearer ${access_token}`;
      
      setIsAuthenticated(true);
      setAdminUser(username);
      
      return { success: true, message };
    } catch (error) {
      console.error("Login failed:", error);
      return { 
        success: false, 
        message: error.response?.data?.detail || "Erreur de connexion" 
      };
    }
  };

  const logout = () => {
    localStorage.removeItem("admin_token");
    setToken(null);
    setIsAuthenticated(false);
    setAdminUser(null);
    delete axios.defaults.headers.common["Authorization"];
  };

  // API methods for admin operations
  const adminAPI = {
    // Contact Info
    getContactInfo: () => axios.get(`${API}/admin/contact-info`),
    updateContactInfo: (data) => axios.put(`${API}/admin/contact-info`, data),
    
    // Services
    getServices: () => axios.get(`${API}/admin/services`),
    createService: (data) => axios.post(`${API}/admin/services`, data),
    updateService: (id, data) => axios.put(`${API}/admin/services/${id}`, data),
    deleteService: (id) => axios.delete(`${API}/admin/services/${id}`),
    
    // Projects
    getProjects: () => axios.get(`${API}/admin/projects`),
    createProject: (data) => axios.post(`${API}/admin/projects`, data),
    updateProject: (id, data) => axios.put(`${API}/admin/projects/${id}`, data),
    deleteProject: (id) => axios.delete(`${API}/admin/projects/${id}`),
    
    // Testimonials
    getTestimonials: () => axios.get(`${API}/admin/testimonials`),
    createTestimonial: (data) => axios.post(`${API}/admin/testimonials`, data),
    updateTestimonial: (id, data) => axios.put(`${API}/admin/testimonials/${id}`, data),
    deleteTestimonial: (id) => axios.delete(`${API}/admin/testimonials/${id}`),
    
    // Contact Submissions
    getContactSubmissions: () => axios.get(`${API}/admin/contact-submissions`),
    updateSubmissionStatus: (id, status) => axios.put(`${API}/admin/contact-submissions/${id}/status?status=${status}`),
    
    // About Info
    getAboutInfo: () => axios.get(`${API}/admin/about`),
    updateAboutInfo: (data) => axios.put(`${API}/admin/about`, data),
    
    // User Management
    getAdminUsers: () => axios.get(`${API}/admin/users`),
    createAdminUser: (data) => axios.post(`${API}/admin/users`, data),
    toggleUserStatus: (id) => axios.put(`${API}/admin/users/${id}/status`),
    deleteAdminUser: (id) => axios.delete(`${API}/admin/users/${id}`),
    changePassword: (data) => axios.put(`${API}/admin/change-password`, data),
    
    // Content Management
    getContent: () => axios.get(`${API}/admin/content`),
    updateContent: (data) => axios.put(`${API}/admin/content`, data),
    
    // Media Management
    getMedia: () => axios.get(`${API}/admin/media`),
    uploadMedia: (formData, config) => axios.post(`${API}/admin/media/upload`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      ...config
    }),
    deleteMedia: (id) => axios.delete(`${API}/admin/media/${id}`),
    getAdminProfile: () => axios.get(`${API}/admin/profile`),
    updateAdminProfile: (data) => axios.put(`${API}/admin/profile`, data),
    updateUserProfile: (id, data) => axios.put(`${API}/admin/users/${id}/profile`, data),
    resetUserPassword: (id, password) => axios.put(`${API}/admin/users/${id}/password`, { password }),
    
    // Image Upload
    uploadProjectImage: (formData) => axios.post(`${API}/admin/upload/project-image`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    }),
    deleteProjectImage: (filename) => axios.delete(`${API}/admin/upload/project-image/${filename}`)
  };

  const value = {
    isAuthenticated,
    isLoading,
    adminUser,
    login,
    logout,
    adminAPI
  };

  return (
    <AdminContext.Provider value={value}>
      {children}
    </AdminContext.Provider>
  );
};